import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Breadcrumbs,
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import JumboSearch from "@jumbo/components/JumboSearch";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { getMenuBoardList, deleteMenuBoard } from "app/services/api/menuBoard";

/**
 * Menu Manager – list of menu boards (Digital Menu Board section).
 * Fetches boards via GET /api/v1/menu-board; Edit/Delete actions with confirmation.
 */
const MenuManagerList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [searchName, setSearchName] = useState("");
  const [menuBoards, setMenuBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const isMountedRef = useRef(true);

  const loadBoards = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMenuBoardList();
      if (isMountedRef.current) {
        setMenuBoards(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setMenuBoards([]);
        enqueueSnackbar(
          t("notifications.wasFailed", {
            defaultValue: "Nepodarilo sa načítať zoznam menu boardov.",
          }),
          { variant: "error" }
        );
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [t, enqueueSnackbar]);

  useEffect(() => {
    isMountedRef.current = true;
    loadBoards();
    return () => {
      isMountedRef.current = false;
    };
  }, [loadBoards]);

  const filteredBoards = useMemo(() => {
    if (!searchName.trim()) return menuBoards;
    const q = searchName.toLowerCase().trim();
    return menuBoards.filter((board) => {
      const name = (board?.boardName ?? board?.name ?? board?.title ?? "")
        .toString()
        .toLowerCase();
      return name.includes(q);
    });
  }, [menuBoards, searchName]);

  const handleAddNewMenuBoard = () => {
    navigate("/customer/dsok/boards/new");
  };

  const handleDeleteConfirm = async (board) => {
    const boardId = board?.id ?? board?._id;
    if (!boardId) {
      enqueueSnackbar(
        t("notifications.wasFailed", { defaultValue: "Chýba ID menu boardu." }),
        { variant: "error" }
      );
      return;
    }
    hideDialog();
    setDeletingId(boardId);
    try {
      await deleteMenuBoard(boardId);
      await loadBoards();
      enqueueSnackbar(
        t("notifications.wasSuccessful", {
          defaultValue: "Menu board bol zmazaný.",
        }),
        { variant: "success" }
      );
    } catch (e) {
      const body = e?.response?.data;
      const message =
        (body && typeof body === "object" && body.message) ||
        (typeof body === "string" ? body : null) ||
        e?.message ||
        t("notifications.wasFailed", { defaultValue: "Zmazanie zlyhalo." });
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  const deleteConfirmation = (board) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSureToDelete", {
        defaultValue: "Naozaj chcete zmazať tento menu board?",
      }),
      onYes: () => handleDeleteConfirm(board),
      onNo: hideDialog,
    });
  };

  const getBoardName = (board) =>
    (board?.boardName ?? board?.name ?? board?.title ?? "").toString() || "—";

  const pageTitle = t("pages.menuManager.title", {
    defaultValue: "Menu Boards",
  });

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          "& a": { color: "text.secondary", textDecoration: "none" },
          "& a:hover": { textDecoration: "underline" },
        }}
      >
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/daily-menu">
          {t("sidebar.menu.dailyMenuList", {
            defaultValue: "Digital Menu Board",
          })}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" mb={3}>
        {pageTitle}
      </Typography>

      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        mb={2}
        spacing={2}
      >
        <Card sx={{ mb: 0, flex: 1, maxWidth: 400 }}>
          <JumboSearch
            page="menuManager"
            onChange={setSearchName}
            sx={{ width: "100%" }}
            placeholder={t("pages.menuManager.searchPlaceholder", {
              defaultValue: "Search by menu board name",
            })}
          />
        </Card>
        <Tooltip
          title={t("pages.menuManager.addMenuBoard", {
            defaultValue: "Add Menu Board",
          })}
        >
          <IconButton onClick={handleAddNewMenuBoard}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredBoards.length > 0 ? (
        <Stack spacing={1}>
          {filteredBoards.map((board) => {
            const boardId = board?.id ?? board?._id;
            return (
              <Card key={boardId} sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack
                    direction="row"
                    spacing={4}
                    alignItems="center"
                    sx={{
                      flex: 1,
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                    component={Link}
                    to={`/customer/dsok/boards/${boardId}`}
                  >
                    <Stack>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {t("pages.menuManager.boardName", {
                          defaultValue: "Board name",
                        })}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {getBoardName(board)}
                      </Typography>
                    </Stack>
                    {typeof board?.isActive === "boolean" && (
                      <Stack>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {t("pages.menuManager.status", {
                            defaultValue: "Status",
                          })}
                        </Typography>
                        <Typography variant="body1">
                          {board.isActive
                            ? t("pages.menuManager.active", {
                                defaultValue: "Active",
                              })
                            : t("pages.menuManager.inactive", {
                                defaultValue: "Inactive",
                              })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={t("buttons.edit")}>
                      <IconButton
                        component={Link}
                        to={`/customer/dsok/boards/${boardId}`}
                        aria-label={t("buttons.edit")}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("buttons.delete")}>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteConfirmation(board);
                        }}
                        disabled={deletingId === boardId}
                        aria-label={t("buttons.delete")}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </>
  );
};

export default MenuManagerList;
